import { CascadiaColor } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useToggleState } from "@react-stately/toggle";
import classNames from "classnames";
import React from "react";
import {
  AriaCheckboxGroupItemProps,
  AriaCheckboxProps,
  VisuallyHidden,
  mergeProps,
  useCheckbox,
  useCheckboxGroupItem,
  useFocusRing,
} from "react-aria";
import { ToggleProps } from "react-stately";

import { CheckboxControl } from "./CheckboxControl";
import { CheckboxContext } from "./context";
import { FormText } from "../../../components/inputs/_assets/input-wrapper/FormText";
import {
  INPUT_HEIGHT,
  INPUT_WIDTH,
  INPUT_WRAPPER_MIN_HEIGHT,
  INPUT_WRAPPER_MIN_WIDTH,
} from "../../../components/inputs/constants";
import { Text } from "../../../foundations/typography/Text";
import { useColors } from "../../../hooks/useColors";

export interface CheckboxProps extends AriaCheckboxProps, ToggleProps {
  subtle?: boolean;
  description?: string | React.ReactElement;
  card?: boolean;
  testID?: string;
}

export interface CheckboxGroupItemProps extends AriaCheckboxGroupItemProps {
  subtle?: boolean;
  description?: string | React.ReactElement;
  card?: boolean;
  testID?: string;
}

export function CheckboxGroupItem(props: CheckboxGroupItemProps) {
  const state = React.useContext(CheckboxContext);
  const ref = React.useRef<HTMLInputElement>(null);
  const { inputProps, isDisabled, isSelected } = useCheckboxGroupItem(
    props,
    state,
    ref
  );

  return (
    <_Checkbox
      inputProps={inputProps}
      isSelected={isSelected}
      isDisabled={isDisabled}
      {...props}
      inputRef={ref}
      card
    />
  );
}

/**
 * @deprecated use CheckboxRAC
 */
export function Checkbox(props: CheckboxProps) {
  const state = useToggleState(props);
  const ref = React.useRef<HTMLInputElement>(null);
  const { inputProps, isDisabled, isSelected } = useCheckbox(props, state, ref);

  return (
    <_Checkbox
      inputProps={inputProps}
      isSelected={isSelected}
      isDisabled={isDisabled}
      {...props}
      inputRef={ref}
    />
  );
}

/**
 * @deprecated use CheckboxRAC
 */
export function _Checkbox(
  props: CheckboxProps & {
    inputProps: React.InputHTMLAttributes<HTMLInputElement>;
    inputRef: React.RefObject<HTMLInputElement>;
  }
) {
  const {
    subtle,
    description,
    children,
    testID = "checkbox",
    card,
    isDisabled,
    isSelected,
  } = props;
  const colors = useColors();
  const styles = getStyles(colors);

  const { isFocusVisible, focusProps } = useFocusRing();

  return (
    <label
      className={classNames(
        css({
          flexDirection: card ? "row-reverse" : "unset",
          display: card ? "flex" : "inline-flex",
          paddingTop: !card ? "var(--space-1)" : undefined,
          paddingBottom: !card ? "var(--space-1)" : undefined,
        }),
        card &&
          css(styles.card, {
            "&:focus-within": {
              boxShadow: "var(--focus-action)",
            },
            cursor: isDisabled ? "not-allowed" : "default",
            backgroundColor: isDisabled
              ? "var(--background-input)"
              : isSelected
                ? "var(--background-input)"
                : "var(--background-input)",
            border: `1px solid ${
              isDisabled
                ? "var(--border-input-disabled)"
                : isSelected
                  ? "var(--border-input-active)"
                  : "var(--border-base)"
            }`,
            "&:focus, &:hover": css({
              outline: "0",
              border: `1px solid ${
                isDisabled
                  ? "var(--border-input-disabled)"
                  : isSelected
                    ? "var(--border-input-hover)"
                    : "var(--border-input-hover)"
              }`,
              backgroundColor: isDisabled
                ? "var(--background-input)"
                : isSelected
                  ? "var(--background-input)"
                  : "var(--background-input)",
            }),
          })
      )}
      data-testid={testID}
    >
      <div
        className={css({
          display: "flex",
          alignItems: card ? "center" : undefined,
        })}
      >
        <VisuallyHidden>
          <input
            {...mergeProps(props.inputProps, focusProps)}
            data-testid={`${testID}-checkbox`}
            ref={props.inputRef}
          />
        </VisuallyHidden>
        <CheckboxControl
          isSelected={isSelected}
          isDisabled={isDisabled}
          isFocusVisible={isFocusVisible}
        />
      </div>

      <div
        className={css(styles.textContent, {
          paddingLeft: !card ? "var(--space-2)" : undefined,
          gap: !card ? "var(--space-1)" : undefined,
        })}
      >
        <Text
          style={subtle ? "input/helper" : "input/label"}
          color="text-base"
          className={styles.labelText}
        >
          {children}
        </Text>
        {description && (
          <FormText className={styles.labelText}>{description}</FormText>
        )}
      </div>
    </label>
  );
}

const getStyles = (colors: Record<CascadiaColor, string>) => {
  return {
    textContent: css({
      flexDirection: "column",
      display: "flex",
    }),
    inputWrapper: css({
      height: INPUT_WRAPPER_MIN_HEIGHT,
      minWidth: INPUT_WRAPPER_MIN_WIDTH,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexShrink: 0,
      position: "relative",
    }),
    labelText: css({
      padding: 0,
      margin: 0,
    }),
    iconWrapper: css({
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",

      i: css({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }),
    }),
    input: css({
      margin: 0,
      width: INPUT_WIDTH,
      height: INPUT_HEIGHT,
      borderRadius: "var(--border-radius-2)",
      "&:checked": css({
        color: "var(--icon-inverse)",
        border: `2px solid ${colors["background-selected-strong"]}`,
      }),
      "&:disabled": css({
        color: "var(--icon-inverse)",
        cursor: "not-allowed",
      }),
      appearance: "none",
    }),
    card: css({
      paddingTop: "var(--space-3)",
      paddingBottom: "var(--space-3)",
      paddingLeft: "var(--space-3)",
      paddingRight: "var(--space-3)",
      borderRadius: "var(--border-radius-3)",
      justifyContent: "space-between",
      "&:focus, &:hover": css({
        outline: "0",
      }),
    }),
  };
};
